import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useWorkspace } from '../context/WorkspaceContext';

/** Number of keys a project can have before subscription is required for translation actions */
export const FREE_TIER_KEY_LIMIT = 10;

export interface Subscription {
  id: string;
  team_id: string;
  status: string;
  product_name?: string;
  renewal_date?: string;
  cancel_at_period_end?: boolean;
}

export const useSubscription = () => {
  const { selectedTeam } = useWorkspace();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!selectedTeam?.id) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data: subscriptionData, error: subError } = await supabase
          .from('Subscriptions')
          .select('id, team_id, status, product_name, renewal_date, cancel_at_period_end')
          .eq('team_id', selectedTeam.id)
          .in('status', ['active', 'trialing'])
          .maybeSingle();

        if (subError) {
          if (subError.code !== 'PGRST116') {
            throw new Error(`Database error: ${subError.message}`);
          }
        }

        setSubscription(subscriptionData as Subscription | null);
      } catch (err: any) {
        setError(err.message);
        setSubscription(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [selectedTeam?.id]);

  // Any active subscription (including free tier) allows editing
  const hasActiveSubscription = subscription !== null &&
    (subscription.status === 'active' || subscription.status === 'trialing') &&
    !subscription.cancel_at_period_end;

  return {
    subscription,
    hasActiveSubscription,
    isLoading,
    error
  };
};
